import {
  type EdgeCenterLabelValue,
  type EdgeData,
  type EdgeLinecapLabelValue,
  type GroupEdgeSettings,
  type ProtocolEdgeType,
  type TopologyEdgeData,
  type TopologyGraphEdgeSettings,
  isCompareGraphOverlay,
  isGroupEdgeSettings,
  isTopologyEdgeData,
} from '@ipfabric/utils';
import { isGroupSettings } from 'components/Graph/GraphState';
import { getEdgeThickness } from 'components/Graph/helpers/getEdgeThickness';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import type { Draft } from 'immer';
import { hexToNumber } from '../GraphApp/helpers/hexToNumber';
import type { GraphEdgeSettingsDiff } from '../model/GraphEdgeSettingsDiff';
import { getEdgeFromGroupByProtocol } from './helpers/getEdgeFromGroupByProtocol';
import type { GraphEdgeSettingsLabelsVisibilityDiff } from './helpers/getLabelsVisibilityDiff';

export interface UpdateGraphDataFromSettingsCommand {
  currentLabel: string | null;
  edgeSettingsDiff?: GraphEdgeSettingsDiff;
  groupId: GroupEdgeSettings['id'] | null;
  edgeSettingsLabelsVisibilityDiff?: GraphEdgeSettingsLabelsVisibilityDiff;
  protocolName?: ProtocolEdgeType;
}

const hasGroupProtocol = (
  group: GroupEdgeSettings,
  edgeData: EdgeData,
): boolean => group.children.some((edge) => edge.name === edgeData.protocol);

const updateEdgeLabelsVisibility = (
  labels: Draft<(EdgeLinecapLabelValue | EdgeCenterLabelValue)[] | undefined>,
  edgeSettingsLabelsVisibilityDiff: GraphEdgeSettingsLabelsVisibilityDiff,
): void => {
  if (labels === undefined) {
    return;
  }
  for (const label of labels) {
    if (
      Object.prototype.hasOwnProperty.call(
        edgeSettingsLabelsVisibilityDiff.updated,
        label.type,
      )
    ) {
      label.visible = edgeSettingsLabelsVisibilityDiff.updated[label.type];
    }
  }
};

const updateEdgeLabelsWrap = (
  labels: Draft<(EdgeLinecapLabelValue | EdgeCenterLabelValue)[] | undefined>,
  wrapText: boolean | undefined,
): void => {
  if (labels === undefined) {
    return;
  }
  for (const label of labels) {
    label.wrapText = wrapText ?? label.wrapText;
  }
};

const isEdgeChanged = (
  item: TopologyGraphEdgeSettings,
  edge: TopologyEdgeData,
): boolean => {
  const itemIsGroup = isGroupSettings(item);
  return (
    (itemIsGroup && item.id === edge.edgeSettingsId) ||
    (!itemIsGroup && item.name === edge.protocol)
  );
};

const isEdgeLabelsChanged = (
  item: TopologyGraphEdgeSettings,
  edge: TopologyEdgeData,
): boolean => {
  const itemIsGroup = isGroupSettings(item);
  return (
    // Item and edge are groups
    (itemIsGroup && item.id === edge.edgeSettingsId) ||
    // Item and edge are protocols
    (!itemIsGroup && item.name === edge.protocol) ||
    // Item is group and edge is protocol
    (isGroupSettings(item) && hasGroupProtocol(item, edge))
  );
};

const updateColor = (
  edge: Draft<EdgeData>,
  edgeSettingsDiff: GraphEdgeSettingsDiff,
) => {
  const changedColor = edgeSettingsDiff.updated.style?.color;
  if (changedColor !== undefined && edge.style?.line?.color !== undefined) {
    edge.style.line.color = hexToNumber(changedColor);
  }
};

const updatePattern = (
  edge: Draft<EdgeData>,
  edgeSettingsDiff: GraphEdgeSettingsDiff,
) => {
  const changedPattern = edgeSettingsDiff.updated.style?.pattern;
  if (changedPattern !== undefined && edge.style?.line?.pattern) {
    edge.style.line.pattern = changedPattern;
  }
};

const updateLabel = (
  edge: Draft<EdgeData>,
  edgeSettingsDiff: GraphEdgeSettingsDiff,
  item: TopologyGraphEdgeSettings,
  currentLabel: string | null,
) => {
  if (item.type === 'group') {
    // Because item is group, diff is also for group
    const updatedSettings =
      edgeSettingsDiff.updated as Partial<GroupEdgeSettings>;
    const changedEdgeLabel = updatedSettings?.label;
    if (
      currentLabel !== null &&
      changedEdgeLabel &&
      edge.labels?.center !== undefined
    ) {
      const foundLabel = edge.labels.center.find(
        (textItem) => textItem.type === 'protocol',
      );
      if (foundLabel !== undefined) {
        foundLabel.text = foundLabel.text.replace(
          currentLabel,
          changedEdgeLabel,
        );
        foundLabel.wrapText =
          updatedSettings.labelsOptions?.wrapCenterLabel ?? foundLabel.wrapText;
      }
    }
  }
};

const updateThickness = (
  edge: TopologyEdgeData,
  edgeSettingsDiff: GraphEdgeSettingsDiff,
  item: TopologyGraphEdgeSettings,
) => {
  const changedThickness = edgeSettingsDiff.updated.style?.thicknessThresholds;
  if (
    changedThickness !== undefined &&
    edge.style?.line?.thickness &&
    edge.children
  ) {
    edge.style.line.thickness = getEdgeThickness(
      item.style.thicknessThresholds,
      edge.children.length,
    );
  }
};

export const updateGraphDataFromSettings: GraphCommandFactory<
  UpdateGraphDataFromSettingsCommand
> = (cmdContext) => async (command) => {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  cmdContext.updateState((draft) => {
    const item = getEdgeFromGroupByProtocol(
      draft,
      command.groupId,
      command.protocolName,
    );
    if (
      command.edgeSettingsDiff === undefined ||
      command.edgeSettingsLabelsVisibilityDiff === undefined ||
      item === undefined
    ) {
      return;
    }
    const edges = Object.values(draft.graph.edges);
    for (const edge of edges) {
      if (isTopologyEdgeData(edge) && isEdgeChanged(item, edge)) {
        if (!isCompareGraphOverlay(cmdContext.getState().overlay)) {
          updateColor(edge, command.edgeSettingsDiff!);
        }
        updatePattern(edge, command.edgeSettingsDiff!);
        updateLabel(
          edge,
          command.edgeSettingsDiff!,
          item,
          command.currentLabel,
        );
        updateThickness(edge, command.edgeSettingsDiff!, item);
      }
      // Update labels
      if (
        isTopologyEdgeData(edge) &&
        isEdgeLabelsChanged(item, edge) &&
        (!isGroupEdgeSettings(item) ||
          (isGroupEdgeSettings(item) && !edge.protocol))
      ) {
        updateEdgeLabelsVisibility(
          edge.labels?.source,
          command.edgeSettingsLabelsVisibilityDiff!,
        );
        updateEdgeLabelsVisibility(
          edge.labels?.target,
          command.edgeSettingsLabelsVisibilityDiff!,
        );
        updateEdgeLabelsVisibility(
          edge.labels?.center,
          command.edgeSettingsLabelsVisibilityDiff!,
        );
        // Update wrap of labels
        const updatedSettings = command.edgeSettingsDiff
          .updated as Partial<GroupEdgeSettings>;
        updateEdgeLabelsWrap(
          edge.labels?.center,
          updatedSettings.labelsOptions?.wrapCenterLabel,
        );
        updateEdgeLabelsWrap(
          edge.labels?.source,
          updatedSettings.labelsOptions?.wrapLinecapLabel,
        );
        updateEdgeLabelsWrap(
          edge.labels?.target,
          updatedSettings.labelsOptions?.wrapLinecapLabel,
        );
      }
    }
  });
};
